/**
 * The WAI-ARIA tree keyboard for the sidebar tree, over its flattened rows
 * with one roving tab stop (the active row):
 * - Up / Down: previous / next visible row.
 * - Right: a collapsed folder expands; an expanded one moves to its first child.
 * - Left: an expanded folder collapses; anything else moves to its parent.
 * - Home / End: first / last row. Enter: open the row.
 *
 * Keys a row already handled (Shift+F10 opening the menu) arrive with
 * `defaultPrevented` and are left alone, as are keys pressed on anything that
 * is not a row (the Show more button inside the tree).
 */

import { useCallback, type KeyboardEvent } from 'react';

import type { TreeRow } from './useFolderTreeRows';

interface TreeKeyboardOptions {
  readonly rows: ReadonlyArray<TreeRow>;
  readonly activeIndex: number;
  readonly setActiveId: (id: string) => void;
  readonly setRowExpanded: (row: TreeRow, open: boolean) => void;
  readonly openRow: (index: number) => void;
}

function nextIndex(rows: ReadonlyArray<TreeRow>, index: number, key: string): number | null {
  const row = rows[index];
  if (!row) return null;
  switch (key) {
    case 'ArrowDown':
      return Math.min(index + 1, rows.length - 1);
    case 'ArrowUp':
      return Math.max(index - 1, 0);
    case 'Home':
      return 0;
    case 'End':
      return rows.length - 1;
    case 'ArrowRight':
      return row.isExpanded && rows[index + 1]?.parentId === row.item.id ? index + 1 : null;
    case 'ArrowLeft': {
      if (row.isExpanded || row.parentId === null) return null;
      const parent = rows.findIndex((r) => r.item.id === row.parentId);
      return parent === -1 ? null : parent;
    }
    default:
      return null;
  }
}

export function useTreeKeyboard({
  rows,
  activeIndex,
  setActiveId,
  setRowExpanded,
  openRow,
}: TreeKeyboardOptions) {
  return useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (event.defaultPrevented || event.altKey || event.metaKey || event.ctrlKey) return;
      // Rows own the tree keys. A control inside the tree that is not a row
      // (the Show more button) keeps its own keys: Enter activates it.
      if (!(event.target instanceof Element) || event.target.getAttribute('role') !== 'treeitem') {
        return;
      }
      const row = rows[activeIndex];
      if (!row) return;
      if (event.key === 'Enter') {
        event.preventDefault();
        openRow(activeIndex);
        return;
      }
      const toggles =
        row.isFolder &&
        ((event.key === 'ArrowRight' && !row.isExpanded) ||
          (event.key === 'ArrowLeft' && row.isExpanded));
      if (toggles) {
        event.preventDefault();
        setRowExpanded(row, event.key === 'ArrowRight');
        return;
      }
      const target = nextIndex(rows, activeIndex, event.key);
      if (target === null) return;
      event.preventDefault();
      const next = rows[target];
      if (next) setActiveId(next.item.id);
    },
    [rows, activeIndex, setActiveId, setRowExpanded, openRow],
  );
}
